import DvrIcon from '@mui/icons-material/Dvr'
import {List} from '@mui/material'
import {ReadyKitPlatform} from '@oc/customer'
import {fullPath, useCustomerRouteContext, useUserPermissionsContext} from '@oc/shell-core'
import {makeOcStyles} from '@oc/styles'
import {observer} from 'mobx-react-lite'
import routesObj from '../routes'
import {messages} from '../translations'
import {CollapsibleItemSelectable, DrawerItem} from './DrawerItem'
import {AccountSubHeader, RkSubHeader} from './Header'

const useListStyles = makeOcStyles(theme => ({
    list: {
        paddingTop: '1px',
        paddingBottom: theme.spacing(1),
        overflowY: 'auto',
        '&::-webkit-scrollbar': {
            width: theme.spacing(1),
            backgroundColor: theme.palette.background.paper
        },
        '&::-webkit-scrollbar-thumb': {
            backgroundColor: theme.syskronPalette.colors.S400,
            border: 'none',
            width: theme.spacing(1)
        }
    }
}))

function _ItemsWithCustomerAndRk() {
    const {desiredAccountId, desiredReadyKit, readyKit} = useCustomerRouteContext()
    const userPermissions = useUserPermissionsContext()
    const {withCustomerAndRk: routes} = routesObj
    const {classes} = useListStyles()

    if (!desiredAccountId || !desiredReadyKit) {
        return null
    }

    const isKopV1 = readyKit?.targetPlatform === ReadyKitPlatform.KOPv1
    const isKopV2 = readyKit?.targetPlatform === ReadyKitPlatform.KOPv2
    const isKop = isKopV1 || isKopV2
    return (
        <>
            <AccountSubHeader />
            <RkSubHeader />
            <List disablePadding={true} className={classes.list}>
                <CollapsibleItemSelectable
                    icon={DvrIcon}
                    route={fullPath(routes.rkMonitoring, routesObj)}
                    label={messages.getText('routes', 'rkMonitoring')}
                    rkId={desiredReadyKit}
                    idPrefix="menu-rk-monitoring"
                    selectable={true}
                >
                    {!isKopV2 && userPermissions.rkMonitoring.canView(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.rkSystemInformation, routesObj)}
                            nestLevel={1}
                        />
                    )}

                    {!isKopV2 && userPermissions.rkUploadLogs.canView(desiredAccountId) && (
                        <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.rkLogs, routesObj)} nestLevel={1} />
                    )}

                    {userPermissions.rkMonitoringConfiguration.canView(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.rkMonitoringConfiguration, routesObj)}
                            nestLevel={1}
                        />
                    )}

                    {userPermissions.dasDiagnostic.canView(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.dasValidation, routesObj)}
                            nestLevel={1}
                        />
                    )}

                    {!isKopV2 && userPermissions.rkRestartServices.canRestartService(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.restartService, routesObj)}
                            nestLevel={1}
                        />
                    )}

                    {!isKopV2 && userPermissions.ntpDiagnostic.canView(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.ntpValidation, routesObj)}
                            nestLevel={1}
                        />
                    )}

                    {!isKopV2 && userPermissions.rkConnectivityCheck.canView(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.connectivityCheck, routesObj)}
                            nestLevel={1}
                        />
                    )}

                    {!isKopV2 &&
                        (userPermissions.rkSsm.canStart(desiredAccountId) ||
                            userPermissions.rkSsmCommand.canView()) && (
                            <DrawerItem
                                rkId={desiredReadyKit}
                                route={fullPath(routes.rkSsm, routesObj)}
                                nestLevel={1}
                            />
                        )}

                    {userPermissions.dataValidation.canView(desiredAccountId) && (
                        <DrawerItem
                            rkId={desiredReadyKit}
                            route={fullPath(routes.dataValidation, routesObj)}
                            nestLevel={1}
                        />
                    )}
                </CollapsibleItemSelectable>

                {userPermissions.edgeStackUpgrade.canView(desiredAccountId) && isKop && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.edgeStack, routesObj)} />
                )}

                {userPermissions.kopVersionControl.canView(desiredAccountId) && isKop && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.kopVersionControl, routesObj)} />
                )}

                {!isKopV2 && userPermissions.networks.canView(desiredAccountId) && (
                    <DrawerItem
                        rkId={desiredReadyKit}
                        route={fullPath(routes.network, routesObj)}
                        hasWarning={readyKit?.hasNetworkDrift || false}
                    />
                )}

                {userPermissions.machinesAndServices.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.hwAndServices, routesObj)} />
                )}

                {userPermissions.interfaces.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.interfaces, routesObj)} />
                )}

                {!isKopV2 && userPermissions.registrations.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.registerRk, routesObj)} />
                )}

                {(userPermissions.dataSources.canView(desiredAccountId) ||
                    userPermissions.datapoints.canView(desiredAccountId) ||
                    userPermissions.messages.canView(desiredAccountId)) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.meda, routesObj)} />
                )}

                {userPermissions.deployments.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.deploy, routesObj)} />
                )}

                {!isKopV2 && userPermissions.passwords.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.passwords, routesObj)} />
                )}

                {userPermissions.configFiles.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.rkConfig, routesObj)} />
                )}

                {userPermissions.decommissioning.canView(desiredAccountId) && (
                    <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.decommissioning, routesObj)} />
                )}

                {import.meta.env.VITE_STAGE !== 'prod' &&
                    userPermissions.opcUaLibraryHandler.canView(desiredAccountId) && (
                        <DrawerItem rkId={desiredReadyKit} route={fullPath(routes.opcuaMapping, routesObj)} />
                    )}
            </List>
        </>
    )
}
export const ItemsWithCustomerAndRk = observer(_ItemsWithCustomerAndRk)
